import type { MouseEventHandler } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useOpenMemoryDetail from '@/hooks/useOpenMemoryDetail';
import useStorage from '@/hooks/useStorage';
import { findParentTypeNodeWithClassName } from '@/utils/UI/html-content';

import { ScrollContainerIdentifier } from './components/scrollContainer';

export const ChatListCitationElementContainerClassName = 'chat-list-citation-element-class-name';

export const ChatListCitationNoteElementContainerClassName =
  'chat-list-citation-note-element-class-name';

export interface IProps {
  parentContainerElement: HTMLDivElement | null;
  popoverElement: HTMLDivElement | null;
}

export interface HoverElementInfo {
  x: number;
  y: number;
  url?: string;
  linkInfo?: string;
  detail?: string;
  element: HTMLElement;
  maxWidth: number;
  minWidth: number;
}

export interface IResult {
  onMouseMove?: MouseEventHandler<HTMLDivElement>;
  hoveredElement?: HoverElementInfo;
}

export default function useCitationPopover(props: IProps): IResult {
  const { popoverElement, parentContainerElement } = props;

  const [hoveredElement, setHoveredElement] = useState<HoverElementInfo>();
  const hoverElementRef = useRef<boolean>(false);
  const hoverPopoverRef = useRef<boolean>(false);
  const [layoutSize] = useStorage('layoutSize');
  const { openMemoryDetail } = useOpenMemoryDetail(layoutSize === LAYOUT_SIZE.SMALL);

  const tryCleanHoveredElement = useCallback(() => {
    setTimeout(() => {
      if (hoverPopoverRef.current || hoverElementRef.current) {
        return;
      }

      if (!hoveredElement) {
        return;
      }

      setHoveredElement(undefined);
    }, 100);
  }, [hoveredElement]);

  useEffect(() => {
    const cb = () => {
      if (!hoverElementRef.current && !hoverPopoverRef.current) {
        return;
      }

      hoverElementRef.current = false;
      hoverPopoverRef.current = false;
      tryCleanHoveredElement();
    };

    const element = document.getElementById(ScrollContainerIdentifier);

    if (!element) {
      return;
    }

    element.addEventListener('scroll', cb);

    return () => {
      element.removeEventListener('scroll', cb);
    };
  }, [tryCleanHoveredElement]);

  useEffect(() => {
    if (!popoverElement) {
      return;
    }

    popoverElement.onmouseenter = () => {
      hoverElementRef.current = true;
    };

    popoverElement.onmouseleave = () => {
      hoverElementRef.current = false;
      tryCleanHoveredElement();
    };
  }, [popoverElement, tryCleanHoveredElement]);

  const findParentNode = useCallback((element: HTMLElement) => {
    // 有可能是 markdown，所以需要找到最近的一层的 最外层的 a/img
    let parentElement: HTMLElement | undefined = undefined;

    parentElement =
      findParentTypeNodeWithClassName({
        fromNode: element,
        className: ChatListCitationElementContainerClassName
      }) ||
      findParentTypeNodeWithClassName({
        fromNode: element,
        className: ChatListCitationNoteElementContainerClassName
      });

    return parentElement;
  }, []);

  const findElementNodeLink = useCallback((element: HTMLElement): [string, string] => {
    if (element.classList.contains(ChatListCitationElementContainerClassName)) {
      return [
        element.getAttribute('data-link') || '',
        decodeURIComponent(element.getAttribute('data-link-info') || '')
      ];
    }

    return ['', ''];
  }, []);

  // noteIndex onClick handler
  const handler = useRef<(e: any) => void>();

  const handleClickNoteIndex = useCallback(
    (detail: string) => (e: any) => {
      e.stopPropagation();

      let citationNote: any;

      try {
        citationNote = JSON.parse(decodeURIComponent(detail));
      } catch (err) {
        citationNote = {};
        console.log('citationNoteError', err);
      }

      openMemoryDetail({
        noteId: citationNote.noteId
      });
    },
    []
  );

  // 找到的同时给index的这个元素赋予onClick事件
  const findElementNodeDetail = useCallback(
    (element: HTMLElement) => {
      if (element.classList.contains(ChatListCitationNoteElementContainerClassName)) {
        const detail = element.getAttribute('data-detail');

        if (detail) {
          // 先移除可能存在的click事件监听器
          if (handler.current) {
            element.removeEventListener('click', handler.current);
          }

          // 创建新的处理函数，并将它保存到引用中
          handler.current = handleClickNoteIndex(detail);
          element.addEventListener('click', handler.current);

          return detail;
        }
      }

      return undefined;
    },
    [handleClickNoteIndex]
  );

  const onMouseLeave = useCallback(() => {
    hoverPopoverRef.current = false;
    tryCleanHoveredElement();
  }, [tryCleanHoveredElement]);

  const onMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      let element = e.target as HTMLElement | undefined;

      if (!parentContainerElement || !popoverElement) {
        onMouseLeave();

        return;
      }

      if (!(element instanceof HTMLElement)) {
        onMouseLeave();

        return;
      }

      element = findParentNode(element);

      if (!element) {
        onMouseLeave();

        return;
      }

      const [link, linkInfo] = findElementNodeLink(element);

      const detailStr = findElementNodeDetail(element);

      if (!link && !detailStr && !linkInfo) {
        onMouseLeave();

        return;
      }

      let linkInfoObj: any = {};

      try {
        linkInfoObj = JSON.parse(decodeURIComponent(linkInfo));
      } catch (err) {
        console.log('citationLinkInfoError', err);
      }

      const showLinkCard = !!linkInfoObj.title || !!linkInfoObj.summary;

      const elementRect = element.getBoundingClientRect();
      const parentRect = parentContainerElement.getBoundingClientRect();

      const offsetY =
        elementRect.y - parentRect.y + parentContainerElement.scrollTop + elementRect.height;

      let offsetX = elementRect.x - parentRect.x + parentContainerElement.scrollLeft;
      const desiredWidth = detailStr || showLinkCard ? 320 : 150; // minWidth
      const maxWidth =
        parentRect.width - offsetX > desiredWidth ? desiredWidth : parentRect.width - offsetX;

      // If desired width is larger than the maximum possible width (maxWidth)
      if (desiredWidth > maxWidth) {
        offsetX -= desiredWidth - maxWidth; // Decrease offsetX by the amount that exceeds
      }

      // Ensure offsetX is not less than 0
      offsetX = Math.max(0, offsetX);

      hoverPopoverRef.current = true;
      setHoveredElement({
        element: element,
        url: link,
        linkInfo: linkInfo || '{}',
        detail: detailStr || '{}',
        x: offsetX,
        y: offsetY,
        maxWidth,
        minWidth: desiredWidth
      });
    },
    [
      findElementNodeDetail,
      findElementNodeLink,
      findParentNode,
      onMouseLeave,
      parentContainerElement,
      popoverElement
    ]
  );

  return { onMouseMove, hoveredElement };
}
